<template>
    <ModalLayout
        visible
        @destroy="onModalClose"
        @success="onModalSubmit"
        :success-loading="loading"
        :tabs="tabItems"
        :tabActive="activeTab"
        @tabSelected="(tab) => activeTab = tab"
    >
        <template v-slot:title>{{ $t('action_codes.form.title') }}</template>

        <template v-slot:settingArea>
            <p>{{ $t('action_codes.form.subtitle') }}</p>

            <g-form :form="form" :action="formUrl" class="form-container"
                    v-slot="{ activeLocale, changeLocale, availableLocales }"
                    :method="formMethod"
                    :prepopulate="formMethod === 'patch'"
                    :before="parseFormData"
                    :after-error="transformErrors"
                    @prepopulated="parseExistingFormData"
                    @loading-state="(state) => loading = state" ref="formRef"
                    @fail="setActiveStepForErrors"
                    @success="formSuccess"
            >
                <accordeon-item
                    :title="$t('action_codes.form.general_settings')"
                    :top-title="$t('accordeon.step', [1])"
                    :active="activeStep === 1"
                    @select="selectStep(1)"
                >
                    <div class="is-flex is-flex-direction-column is-justify-content-space-between form-min-height">
                        <div>
                            <label class="group-label">{{ $t('action_codes.form.label.action_type') }}</label>
                            <div class="field-body">
                                <g-select name="type" :label="$t('action_codes.form.type')" :options="actionOptions"
                                          :disabled="id !== null"
                                          :readonly="id !== null"/>
                                <g-input name="name" :label="$t('action_codes.form.name')"/>
                            </div>

                            <label class="group-label">{{ $t('action_codes.form.label.action_code') }}</label>
                            <g-input name="code" :label="$t('action_codes.form.code')"/>

                            <label class="group-label">
                                {{ $t('action_codes.form.label.usage_limit') }}
                                <small v-if="actionCodePrice > 0 && form.type === 'bonus_gift_card'">
                                    {{ $t('action_codes.form.cost_notice', {price: formatCurrency(actionCodePrice)}) }}
                                </small>
                            </label>
                            <g-input type="number" name="max_uses" :label="$t('action_codes.form.max_uses')"/>

                            <label class="group-label">{{ $t('action_codes.form.label.validity') }}</label>
                            <div class="field-body">
                                <g-datepicker name="valid_from" :label="$t('action_codes.form.valid_from')"/>
                                <g-datepicker name="valid_until" :label="$t('action_codes.form.valid_until')"
                                              disable-past/>
                            </div>
                            <g-switch name="is_active" :label="$t('action_codes.form.active')"/>
                        </div>

                        <div>
                            <g-button @click.prevent="nextStepOrSubmit" :is-rounded="false" class="is-pulled-right">
                                {{ $t('accordeon.next') }}
                            </g-button>
                        </div>
                    </div>
                </accordeon-item>

                <!-- Step 2: Bonus Gift Card Settings -->
                <accordeon-item
                    v-if="form.type === 'bonus_gift_card'"
                    :title="$t('action_codes.form.type_gift_card')"
                    :top-title="$t('accordeon.step', [2])"
                    :active="activeStep === 2"
                    @select="selectStep(2)">
                    <div class="is-flex is-flex-direction-column is-justify-content-space-between form-min-height">
                        <div>
                            <label class="group-label">
                                {{ $t('action_codes.form.label.reward') }}
                                <small>{{ $t('action_codes.form.label.reward_subtitle') }}</small>
                            </label>
                            <div class="field-body">
                                <g-select name="parameters.reward_type"
                                          :label="$t('action_codes.form.reward_type')"
                                          :options="rewardOptions"/>
                                <g-format
                                    key="g-format-reward_value_fixed_amount"
                                    v-if="form['parameters.reward_type'] === 'fixed_amount'"
                                    name="parameters.reward_value_fixed_amount"
                                    type="currency"
                                    :label="$t('action_codes.form.reward_value')"/>
                                <g-format
                                    key="g-format-reward_value_percentage"
                                    v-if="form['parameters.reward_type'] === 'percentage'"
                                    name="parameters.reward_value_percentage"
                                    type="percentage"
                                    :label="$t('action_codes.form.reward_value')"/>
                                <g-select
                                    v-if="form['parameters.reward_type'] === 'package'"
                                    name="parameters.reward_value_package"
                                    :options="packageOptions"
                                    :label="$t('action_codes.form.reward_value')"/>
                            </div>

                            <g-select :options="expirationOptions" name="parameters.reward_expiration"
                                      :label="$t('action_codes.form.expiration')"/>

                            <label class="group-label">
                                {{ $t('action_codes.form.label.qualification_criteria') }}
                                <small>{{ $t('action_codes.form.label.qualification_criteria_subtitle') }}</small>
                            </label>
                            <div class="field-body">
                                <g-select name="parameters.criteria.0.type"
                                          :label="$t('action_codes.form.criteria_type')"
                                          :options="criteriaOptions"/>
                                <g-format
                                    v-if="form['parameters.criteria.0.type'] === 'min_amount'"
                                    name="parameters.criteria.0.minimum_amount"
                                    type="currency"
                                    :label="$t('action_codes.form.criteria_value_min_gift_card_amount')"/>
                                <g-select
                                    v-if="form['parameters.criteria.0.type'] === 'package'"
                                    name="parameters.criteria.0.package_ids"
                                    :options="packageOptions"
                                    :label="$t('action_codes.form.criteria_value_is_package')" :multiple="true"/>
                            </div>

                            <label class="group-label">
                                {{ $t('action_codes.form.label.reporting_labels') }}
                                <small>{{ $t('action_codes.form.label.reporting_labels_subtitle_bonus') }}</small>
                            </label>
                            <tags-input v-model="editableTag"
                                        :tags="form['parameters.reporting_tags']"
                                        :add-on-key="[';', ',', 13]"
                                        :max-tags="10"
                                        :placeholder="$t('action_codes.form.label.reporting_labels')"
                                        :autocomplete-items="tagsAutocomplete"
                                        :autocomplete-min-length="0"
                                        autocomplete="off"
                                        class="mb-3"
                                        @tags-changed="newTags => form['parameters.reporting_tags'] = newTags"/>

                        </div>

                        <div>
                            <g-button @click.prevent="nextStepOrSubmit" :is-rounded="false" class="is-pulled-right">
                                {{ $t('accordeon.next') }}
                            </g-button>
                        </div>
                    </div>
                </accordeon-item>

                <!-- Step 2: Free Shipping Settings -->
                <accordeon-item
                    v-if="form.type === 'free_shipping'"
                    :title="$t('action_codes.form.type_free_shipping')"
                    :top-title="$t('accordeon.step', [2])"
                    :active="activeStep === 2"
                    @select="selectStep(2)">
                    <div class="is-flex is-flex-direction-column is-justify-content-space-between form-min-height">
                        <div>
                            <label class="group-label">
                                {{ $t('action_codes.form.shipping_coverage') }}
                            </label>
                            <g-radio-cards
                                name="parameters.coverage"
                                :options="coverageOptionsWithDescriptions"
                                compact
                            />

                            <label class="group-label mt-4">
                                {{ $t('action_codes.form.label.order_requirements') }}
                                <small>{{ $t('action_codes.form.label.order_requirements_subtitle') }}</small>
                            </label>
                            <div class="field-body">
                                <g-format
                                    name="parameters.minimum_order_value"
                                    type="currency"
                                    :label="$t('action_codes.form.minimum_order_value')"/>
                                <g-format
                                    name="parameters.maximum_order_value"
                                    type="currency"
                                    :label="$t('action_codes.form.maximum_order_value')"/>
                            </div>
                            <div class="field-body">
                                <g-input
                                    type="number"
                                    name="parameters.minimum_item_count"
                                    :label="$t('action_codes.form.minimum_item_count')"/>
                                <g-input
                                    type="number"
                                    name="parameters.maximum_item_count"
                                    :label="$t('action_codes.form.maximum_item_count')"/>
                            </div>
                            <g-switch
                                name="parameters.first_time_customer"
                                :label="$t('action_codes.form.label.first_time_customer')"
                                :subtitle="$t('action_codes.form.label.first_time_customer_subtitle')"
                            />

                            <label class="group-label mt-4">
                                {{ $t('action_codes.form.label.reporting_labels') }}
                                <small>{{ $t('action_codes.form.label.reporting_labels_subtitle_shipping') }}</small>
                            </label>
                            <tags-input v-model="editableTag"
                                        :tags="form['parameters.reporting_tags']"
                                        :add-on-key="[';', ',', 13]"
                                        :max-tags="10"
                                        :placeholder="$t('action_codes.form.label.reporting_labels')"
                                        :autocomplete-items="tagsAutocomplete"
                                        :autocomplete-min-length="0"
                                        autocomplete="off"
                                        class="mb-3"
                                        @tags-changed="newTags => form['parameters.reporting_tags'] = newTags"/>
                        </div>
                    </div>
                </accordeon-item>

                <!-- Step 2: Order Discount Settings -->
                <accordeon-item
                    v-if="form.type === 'order_discount'"
                    :title="$t('action_codes.form.type_order_discount')"
                    :top-title="$t('accordeon.step', [2])"
                    :active="activeStep === 2"
                    @select="selectStep(2)">
                    <div class="is-flex is-flex-direction-column is-justify-content-space-between form-min-height">
                        <div>
                            <label class="group-label">
                                {{ $t('action_codes.form.label.discount') }}
                                <small>{{ $t('action_codes.form.label.discount_subtitle') }}</small>
                            </label>
                            <div class="field-body">
                                <g-select name="parameters.discount_type"
                                          :label="$t('action_codes.form.discount_type')"
                                          :options="discountTypeOptions"/>
                                <g-format
                                    v-if="form['parameters.discount_type'] === 'percentage'"
                                    key="g-format-discount_value_percentage"
                                    name="parameters.discount_value_percentage"
                                    type="percentage"
                                    :label="$t('action_codes.form.discount_value')"/>
                                <g-format
                                    v-if="form['parameters.discount_type'] === 'fixed'"
                                    key="g-format-discount_value_fixed"
                                    name="parameters.discount_value_fixed"
                                    type="currency"
                                    :label="$t('action_codes.form.discount_value')"/>
                            </div>

                            <template v-if="form['parameters.discount_type'] === 'percentage'">
                                <label class="group-label mt-1">
                                    {{ $t('action_codes.form.label.maximum_discount') }}
                                    <small>{{ $t('action_codes.form.label.maximum_discount_subtitle') }}</small>
                                </label>
                                <g-format
                                    name="parameters.maximum_discount"
                                    type="currency"
                                    :label="$t('action_codes.form.maximum_discount')"/>
                            </template>

                            <label class="group-label mt-4">
                                {{ $t('action_codes.form.label.order_requirements') }}
                                <small>{{ $t('action_codes.form.label.order_requirements_subtitle') }}</small>
                            </label>
                            <div class="field-body">
                                <g-format
                                    name="parameters.minimum_order_value"
                                    type="currency"
                                    :label="$t('action_codes.form.minimum_order_value')"/>
                                <g-format
                                    name="parameters.maximum_order_value"
                                    type="currency"
                                    :label="$t('action_codes.form.maximum_order_value')"/>
                            </div>
                            <div class="field-body">
                                <g-input
                                    type="number"
                                    name="parameters.minimum_item_count"
                                    :label="$t('action_codes.form.minimum_item_count')"/>
                                <g-input
                                    type="number"
                                    name="parameters.maximum_item_count"
                                    :label="$t('action_codes.form.maximum_item_count')"/>
                            </div>
                            <g-switch
                                name="parameters.first_time_customer"
                                :label="$t('action_codes.form.label.first_time_customer')"
                                :subtitle="$t('action_codes.form.label.first_time_customer_subtitle')"
                            />

                            <label class="group-label mt-4">
                                {{ $t('action_codes.form.label.reporting_labels') }}
                                <small>{{ $t('action_codes.form.label.reporting_labels_subtitle_discount') }}</small>
                            </label>
                            <tags-input v-model="editableTag"
                                        :tags="form['parameters.reporting_tags']"
                                        :add-on-key="[';', ',', 13]"
                                        :max-tags="10"
                                        :placeholder="$t('action_codes.form.label.reporting_labels')"
                                        :autocomplete-items="tagsAutocomplete"
                                        :autocomplete-min-length="0"
                                        autocomplete="off"
                                        class="mb-3"
                                        @tags-changed="newTags => form['parameters.reporting_tags'] = newTags"/>
                        </div>
                    </div>
                </accordeon-item>

                <!-- Step 3: Email Settings (only for bonus_gift_card) -->
                <accordeon-item
                    v-if="form.type === 'bonus_gift_card'"
                    :title="$t('action_codes.form.email_settings')"
                    :top-title="$t('accordeon.step', [3])"
                    :active="activeStep === 3"
                    @select="selectStep(3)">

                    <div class="is-flex is-flex-direction-column is-justify-content-space-between form-min-height">
                        <div>
                            <label class="group-label">{{ $t('action_codes.form.label.modify_language') }}</label>
                            <g-select
                                name="locale"
                                :value="activeLocale"
                                @input="changeLocale"
                                :options="availableLocales"
                            />

                            <label class="group-label">{{ $t('action_codes.email.sender') }}</label>
                            <div class="field-body">
                                <g-input name="parameters.sender_name" :label="$t('action_codes.email.name')"/>
                                <g-input name="parameters.sender_email" :label="$t('action_codes.email.email')"/>
                            </div>

                            <label class="group-label">
                                {{ $t('action_codes.email.subject') }}
                            </label>
                            <g-input name="parameters.subject" :label="$t('action_codes.email.subject')" translatable/>

                            <label class="group-label">
                                {{ $t('action_codes.email.message') }}
                                <small>
                                    {{ $t('action_codes.email.message_notice') }}
                                </small>
                            </label>
                            <g-input type="textarea" name="parameters.message" translatable
                                     :label="$t('action_codes.email.message_contents')" autosize/>
                        </div>
                    </div>
                </accordeon-item>
            </g-form>
        </template>

        <template v-slot:previewArea>
            <tabbed-view @tabsInitialized="(tabs) => tabItems = tabs" ref="tabbedView">
                <tabbed-view-item
                    :title="$t('action_codes.form.preview')"
                    :active="activeTab === 0"
                >
                    <!-- Email preview for bonus_gift_card type -->
                    <email-view-item
                        v-if="form.type === 'bonus_gift_card'"
                        :mail-template="mailTemplate"
                        :sender-name="form['parameters.sender_name']"
                        :sender-email="form['parameters.sender_email']"
                        :subject="form[`parameters.subject.${form.activeLocale}`]"
                        :attachments="[$t('action_codes.email.sample.attachment')]"
                    >
                        <template v-slot:header v-if="mailTemplate">
                            <img v-if="mailTemplate.logo" :src="mailTemplate.logo" alt="Company logo"/>
                            <img v-else-if="mailTemplate.company_id === null && companyLogo" :src="companyLogo"
                                 alt="Company logo"/>
                        </template>
                        <template v-slot:body>{{ form[`parameters.message.${form.activeLocale}`] }}</template>
                    </email-view-item>

                    <!-- Placeholder for free_shipping type (no email preview applicable) -->
                    <div v-else-if="form.type === 'free_shipping'" class="preview-placeholder">
                        <div class="preview-placeholder-content">
                            <i class="icon">local_shipping</i>
                            <p class="preview-placeholder-title">{{
                                    $t('action_codes.form.preview_not_applicable_title')
                                }}</p>
                        </div>
                    </div>

                    <!-- Placeholder for order_discount type (no email preview applicable) -->
                    <div v-else-if="form.type === 'order_discount'" class="preview-placeholder">
                        <div class="preview-placeholder-content">
                            <i class="icon">sell</i>
                            <p class="preview-placeholder-title">{{
                                    $t('action_codes.form.preview_not_applicable_title')
                                }}</p>
                        </div>
                    </div>
                </tabbed-view-item>
            </tabbed-view>
        </template>
    </ModalLayout>
</template>

<script>
import ModalLayout from "../ModalLayout.vue";
import {mapGetters} from "vuex";
import EmailViewItem from "./DigitalOrders/EmailViewItem.vue";
import TabbedViewItem from "../TabbedViewItem.vue";
import TabbedView from "../TabbedView.vue";
import AccordeonItem from "../AccordeonItem.vue";
import TagsInput from "@johmun/vue-tags-input";

export default {
    components: {AccordeonItem, TabbedView, TabbedViewItem, EmailViewItem, ModalLayout, TagsInput},
    props: {
        id: {
            type: String,
            default: null
        },
        mailTemplate: {
            type: Object,
            required: true
        },
        price: {
            type: Number,
            default: 0
        },
    },
    computed: {
        ...mapGetters({
            packages: 'packages/all',
            reportingTags: 'promotionalOrders/getReportingTags',
            locales: 'application/availableLocalesTranslated',
        }),
        companyLogo() {
            return this.$store.getters['user/activeCompany'].logo;
        },
        actionCodePrice() {
            return this.price;
        },
        formUrl() {
            if (this.id) {
                return `/api/action-codes/${this.id}`;
            }

            return '/api/action-codes';
        },
        formMethod() {
            if (this.id) {
                return 'patch';
            }

            return 'post';
        },
        actionOptions() {
            return [
                {
                    name: this.$t('action_codes.form.type_gift_card'),
                    value: 'bonus_gift_card'
                },
                {
                    name: this.$t('action_codes.form.type_free_shipping'),
                    value: 'free_shipping'
                },
                {
                    name: this.$t('action_codes.form.type_order_discount'),
                    value: 'order_discount'
                }
            ]
        },
        discountTypeOptions() {
            return [
                {
                    name: this.$t('action_codes.form.discount_type_percentage'),
                    value: 'percentage'
                },
                {
                    name: this.$t('action_codes.form.discount_type_fixed'),
                    value: 'fixed'
                }
            ]
        },
        coverageOptions() {
            return [
                {
                    name: this.$t('action_codes.form.coverage_all_methods'),
                    value: 'all_methods'
                },
                {
                    name: this.$t('action_codes.form.coverage_cheapest_only'),
                    value: 'cheapest_only'
                }
            ]
        },
        coverageOptionsWithDescriptions() {
            return [
                {
                    name: this.$t('action_codes.form.coverage_all_methods'),
                    value: 'all_methods',
                    description: this.$t('action_codes.form.coverage_all_methods_description')
                },
                {
                    name: this.$t('action_codes.form.coverage_cheapest_only'),
                    value: 'cheapest_only',
                    description: this.$t('action_codes.form.coverage_cheapest_only_description')
                }
            ]
        },
        packageOptions() {
            return this.packages.map((option) => {
                return {
                    name: option.title,
                    value: option.id
                }
            });
        },
        rewardOptions() {
            let options = [
                {value: 'fixed_amount', name: this.$t('action_codes.form.reward_type_fixed_amount')},
                {value: 'percentage', name: this.$t('action_codes.form.reward_type_percentage')},
            ]

            if (this.packages.length > 0) {
                options.push({
                    value: 'package',
                    name: this.$t('action_codes.form.reward_type_package')
                })

                this.form.reward_value_package = this.packages[0].id
            }

            return options;
        },
        expirationOptions() {
            const monthsOptions = Array.from({length: 11}, (v, k) => ({
                name: this.$tc('action_codes.form.expiration_months', k + 1),
                value: 30 * (k + 1),
            }));

            const yearsOptions = Array.from({length: 10}, (v, k) => ({
                name: this.$tc('action_codes.form.expiration_years', k + 1),
                value: 365 * (k + 1),
            }));

            return [...monthsOptions, ...yearsOptions];
        },
        criteriaOptions() {
            let options = [
                {value: 'min_amount', name: this.$t('action_codes.form.criteria_type_min_gift_card_amount')},
            ]

            if (this.packages.length > 0) {
                options.push({
                    value: 'package',
                    name: this.$t('action_codes.form.criteria_type_is_package')
                })
            }

            return options;
        },
        tagsAutocomplete() {
            return this.reportingTags.map(tag => {
                return {
                    text: tag,
                }
            })
        },
        maxSteps() {
            return this.form.type === 'bonus_gift_card' ? 3 : 2;
        },
    },
    data() {
        return {
            loading: false,
            activeTab: 0,
            activeStep: 1,
            tabItems: [],
            editableTag: '',
            fieldStepMap: {
                1: [
                    'type',
                    'name',
                    'code',
                    'valid_from',
                    'valid_until',
                    'max_uses',
                    'is_active',
                ],
                2: [
                    // Bonus gift card fields
                    'parameters.reward_type',
                    'parameters.reward_value_fixed_amount',
                    'parameters.reward_value_percentage',
                    'parameters.reward_value_package',
                    'parameters.reward_expiration',
                    'parameters.criteria.0.type',
                    'parameters.criteria.0.minimum_amount',
                    'parameters.criteria.0.package_ids',
                    'parameters.reporting_tags',
                    // Free shipping fields
                    'parameters.coverage',
                    // Order discount fields
                    'parameters.discount_type',
                    'parameters.discount_value',
                    'parameters.maximum_discount',
                    // Order requirements (constraints)
                    'parameters.minimum_order_value',
                    'parameters.maximum_order_value',
                    'parameters.minimum_item_count',
                    'parameters.maximum_item_count',
                    'parameters.first_time_customer',
                ],
                3: [
                    'parameters.sender_name',
                    'parameters.sender_email',
                    'parameters.subject.nl',
                    'parameters.message.nl',
                    'parameters.subject.en',
                    'parameters.message.en',
                ],
            },
            form: new Form({
                locale: this.$i18n.locale,
                type: 'bonus_gift_card',
                name: '',
                code: '',
                valid_from: new Date(),
                valid_until: '',
                max_uses: 500,
                is_active: true,

                'parameters.reporting_tags': [],

                'parameters.reward_type': 'fixed_amount',
                'parameters.reward_value_fixed_amount': '',
                'parameters.reward_value_percentage': '',
                'parameters.reward_value_package': '',
                'parameters.reward_expiration': this.$store.getters['user/activeCompany'].gift_card_expiration,

                'parameters.criteria.0.type': 'min_amount',
                'parameters.criteria.0.minimum_amount': '',
                'parameters.criteria.0.package_ids': [],

                'parameters.minimum_order_value': '',
                'parameters.maximum_order_value': '',
                'parameters.minimum_item_count': '',
                'parameters.maximum_item_count': '',
                'parameters.first_time_customer': '',

                // Free shipping parameters
                'parameters.coverage': 'cheapest_only',

                // Order discount parameters
                'parameters.discount_type': 'percentage',
                'parameters.discount_value': '',
                'parameters.discount_value_percentage': '',
                'parameters.discount_value_fixed': '',
                'parameters.maximum_discount': '',

                'parameters.sender_name': '',
                'parameters.sender_email': '',
                'parameters.subject': {
                    translatable: true,
                    useFallback: true,
                    default: {
                        nl: this.$t('action_codes.email.sample.subject', 'nl'),
                        en: this.$t('action_codes.email.sample.subject', 'en')
                    }
                },
                'parameters.message': {
                    translatable: true,
                    useFallback: true,
                    default: {
                        nl: this.$t('action_codes.email.sample.message', 'nl'),
                        en: this.$t('action_codes.email.sample.message', 'en')
                    }
                }
            }, {
                locales: this.$store.getters['application/availableLocales']
            })
        }
    },
    created() {
        // Load related data for the form
        this.$store.dispatch('packages/loadAll').then(() => {
            if (this.packages.length > 0) {
                this.form['parameters.reward_value_package'] = this.packages[0].id
            }
        })
        this.$store.dispatch('promotionalOrders/loadAll')

        // Set default form values
        this.form.code = this.generateRandomActionCode()

        this.form['parameters.sender_name'] = this.$store.getters['user/user'].first_name
        this.form['parameters.sender_email'] = this.$store.getters['user/user'].email
    },
    methods: {
        formatCurrency(cents) {
            const euros = cents / 100;
            return new Intl.NumberFormat(this.$i18n.locale, {
                style: 'currency',
                currency: 'EUR'
            }).format(euros);
        },
        onModalSubmit() {
            this.$refs.formRef.submit()
        },
        onModalClose() {
            this.$emit('destroy');
        },
        selectStep(step) {
            if (this.activeStep === step) {
                step = step + 1;
            }

            if (step > this.maxSteps) {
                step = 1;
            }

            this.activeStep = step;
        },
        nextStepOrSubmit() {
            if (this.activeStep < this.maxSteps) {
                this.activeStep++;
            } else {
                this.onModalSubmit();
            }
        },
        async parseFormData(form) {
            // Convert currency values to cents
            if (form['parameters.reward_value_fixed_amount']) {
                form['parameters.reward_value_fixed_amount'] = form['parameters.reward_value_fixed_amount'].replace(',', '.');
                form['parameters.reward_value_fixed_amount'] = parseFloat(form['parameters.reward_value_fixed_amount']) * 100;
            }

            if (form['parameters.criteria.0.minimum_amount']) {
                form['parameters.criteria.0.minimum_amount'] = form['parameters.criteria.0.minimum_amount'].replace(',', '.');
                form['parameters.criteria.0.minimum_amount'] = parseFloat(form['parameters.criteria.0.minimum_amount']) * 100;
            }

            // Convert minimum order value for free shipping and order discount
            if (form['parameters.minimum_order_value']) {
                form['parameters.minimum_order_value'] = form['parameters.minimum_order_value'].replace(',', '.');
                form['parameters.minimum_order_value'] = parseFloat(form['parameters.minimum_order_value']) * 100;
            }

            // Convert order discount values
            if (form['parameters.discount_type'] === 'percentage') {
                const percentageValue = form['parameters.discount_value_percentage'];
                form['parameters.discount_value'] = parseInt(percentageValue ?? 0);
            }

            if (form['parameters.discount_type'] === 'fixed') {
                const fixedValue = form['parameters.discount_value_fixed'];
                const cleanedValue = String(fixedValue).replace(',', '.');
                form['parameters.discount_value'] = Math.round(parseFloat(cleanedValue) * 100);
            }

            if (form['parameters.maximum_discount'] && form['parameters.discount_type'] === 'percentage') {
                form['parameters.maximum_discount'] = form['parameters.maximum_discount'].replace(',', '.');
                form['parameters.maximum_discount'] = parseFloat(form['parameters.maximum_discount']) * 100;
            }

            if (form['parameters.maximum_order_value']) {
                form['parameters.maximum_order_value'] = form['parameters.maximum_order_value'].replace(',', '.');
                form['parameters.maximum_order_value'] = parseFloat(form['parameters.maximum_order_value']) * 100;
            }

            // Remove criteria fields for types that don't use criteria (order_discount and free_shipping)
            // These types use parameters.minimum_order_value directly instead of the criteria system
            if (form.type === 'order_discount' || form.type === 'free_shipping') {
                delete form['parameters.criteria.0.type'];
                delete form['parameters.criteria.0.minimum_amount'];
                delete form['parameters.criteria.0.package_ids'];
            }

            // Format date objects to ISO
            // valid_from: start of day (midnight)
            form.valid_from = form.valid_from ? form.valid_from.toISOString() : null;
            // valid_until: end of day (23:59:59) so the code is valid for the entire day the user selected
            if (form.valid_until) {
                const endOfDay = new Date(form.valid_until);
                endOfDay.setHours(23, 59, 59, 999);
                form.valid_until = endOfDay.toISOString();
            }

            // Format boolean to integer
            form.is_active = form.is_active ? 1 : 0;

            return form;
        },
        parseExistingFormData(response) {
            // Convert currency values from cents
            if (this.form['parameters.reward_value_fixed_amount']) {
                this.form['parameters.reward_value_fixed_amount'] /= 100;
            }

            if (this.form['parameters.criteria.0.minimum_amount']) {
                this.form['parameters.criteria.0.minimum_amount'] /= 100;
            }

            if (this.form['parameters.minimum_order_value']) {
                this.form['parameters.minimum_order_value'] /= 100;
            }

            if (this.form['parameters.maximum_discount']) {
                this.form['parameters.maximum_discount'] /= 100;
            }

            if (this.form['parameters.maximum_order_value']) {
                this.form['parameters.maximum_order_value'] /= 100;
            }

            const discountValue = this.form['parameters.discount_value'];
            if (discountValue) {
                if (this.form['parameters.discount_type'] === 'percentage') {
                    this.form['parameters.discount_value_percentage'] = discountValue;
                } else if (this.form['parameters.discount_type'] === 'fixed') {
                    this.form['parameters.discount_value_fixed'] = discountValue / 100;
                }
            }

            this.form.valid_from = new Date(this.form.valid_from)
            this.form.valid_until = this.form.valid_until ? new Date(this.form.valid_until) : null

            // Manually set the package_ids as the component expects an array: data.data.parameters.criteria.0.package_ids
            if (response.data.parameters.criteria && response.data.parameters.criteria[0]) {
                this.form['parameters.criteria.0.package_ids'] = response.data.parameters.criteria[0].package_ids ?? [];
            }

            // Manually set the reporting tags as the component expects an array: data.data.parameters.reporting_tags
            const tags = response.data.parameters.reporting_tags ?? [];
            this.form['parameters.reporting_tags'] = tags.map(tag => {
                return {
                    text: tag,
                }
            })
        },
        formSuccess() {
            this.$buefy.toast.open({
                message: this.$t('action_codes.toast.saved'),
                type: 'is-success',
                duration: 5000,
            })

            this.destroy()
        },
        setActiveStepForErrors(data) {
            // If data object does not contain an errors object, return
            if (!data.errors) {
                return;
            }

            let formErrorFields = [];

            // Loop through the errors object, it has the format of {field: [errors]}
            for (const field in data.errors) {
                // If the field is not in the form object, continue
                if (!this.form.hasOwnProperty(field)) {
                    continue;
                }

                // Add the field to the formErrorFields array
                formErrorFields.push(field);
            }

            let stepsWithErrors = [];

            // Loop through the fieldStepMap object
            for (const step in this.fieldStepMap) {
                // Loop through the fields in the fieldStepMap object
                for (const field of this.fieldStepMap[step]) {
                    // If the field is in the formErrorFields array, add the step to the stepsWithErrors array
                    if (formErrorFields.includes(field)) {
                        stepsWithErrors.push(parseInt(step));
                        break;
                    }
                }
            }

            // If the stepsWithErrors array is not empty, set the activeStep to the lowest step
            if (stepsWithErrors.length > 0) {
                this.activeStep = Math.min(...stepsWithErrors);
            }
        },
        generateRandomActionCode() {
            return 'AC' + Math.random().toString(36).substring(2, 8).toUpperCase();
        },
        transformErrors(errors) {
            // Map server field names back to form field names
            // The server returns 'parameters.discount_value' but we have separate fields for percentage/fixed
            if (errors['parameters.discount_value']) {
                if (this.form['parameters.discount_type'] === 'percentage') {
                    errors['parameters.discount_value_percentage'] = errors['parameters.discount_value']
                } else {
                    errors['parameters.discount_value_fixed'] = errors['parameters.discount_value']
                }
                delete errors['parameters.discount_value']
            }

            return errors
        },
    }
}
</script>

<style scoped lang="scss">
.form-min-height {
    min-height: 100%;
}

.form-container {
    display: flex;
    flex: 1;
    flex-direction: column;
    overflow: hidden;
}

.preview-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    min-height: 300px;
    background: #f9fafb;
    border-radius: 8px;
    border: 1px dashed #e5e7eb;
}

.preview-placeholder-content {
    text-align: center;
    padding: 2rem;

    .icon {
        font-size: 3rem;
        color: #9ca3af;
        margin-bottom: 1rem;
    }
}

.preview-placeholder-title {
    font-size: 1.125rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 0.5rem;
}
</style>
